import { Multiselect } from "@dashboard/components/Combobox";
import { ConfirmButton, ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DashboardModal } from "@dashboard/components/Modal";
import { WsmErrorFragment, WsmFitmentProductSchemaFragment } from "@dashboard/graphql";
import { buttonMessages } from "@dashboard/intl";
import { Box, Button, Option, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { useIntl } from "react-intl";

import AxisValueSelect from "./AxisValueSelect";
import { fitmentMessages } from "./messages";
import { filterOptions, formatWsmError, onePerQualifier, schemaAxesInOrder } from "./utils";

export interface ProductFitmentAddApplicationDialogProps {
  open: boolean;
  schema: WsmFitmentProductSchemaFragment;
  /** Every qualifier value in the vocabulary, labelled "Qualifier: value". */
  qualifierValueOptions: Option[];
  /** Qualifier value id to its qualifier's id. */
  qualifierOf: ReadonlyMap<string, string>;
  confirmButtonState: ConfirmButtonTransitionState;
  errors: WsmErrorFragment[];
  onClose: () => void;
  onSubmit: (values: string[], qualifierValues: string[]) => void;
}

/**
 * One value per axis (an empty axis is unconstrained) plus optional qualifier
 * values, at most one per qualifier (picking a second value of a qualifier
 * replaces the first, as the server would refuse both). The card creates the application (idempotent by its value set) and
 * adds it to its unsaved set; the card's Save puts it on the product.
 */
export const ProductFitmentAddApplicationDialog = ({
  open,
  schema,
  qualifierValueOptions,
  qualifierOf,
  confirmButtonState,
  errors,
  onClose,
  onSubmit,
}: ProductFitmentAddApplicationDialogProps) => {
  const intl = useIntl();
  const axes = React.useMemo(() => schemaAxesInOrder(schema), [schema]);
  const [values, setValues] = React.useState<Record<string, Option | null>>({});
  const [qualifiers, setQualifiers] = React.useState<Option[]>([]);
  const [qualifierQuery, setQualifierQuery] = React.useState("");
  const [touched, setTouched] = React.useState(false);
  const picked = axes.map(axis => values[axis.id]?.value).filter((id): id is string => !!id);

  React.useEffect(() => {
    if (open) {
      setValues({});
      setQualifiers([]);
      setQualifierQuery("");
      setTouched(false);
    }
  }, [open]);

  const handleSubmit = () => {
    setTouched(true);

    if (picked.length === 0) {
      return;
    }

    onSubmit(
      picked,
      qualifiers.map(option => option.value),
    );
  };

  return (
    <DashboardModal open={open} onChange={onClose}>
      <DashboardModal.Content size="sm" data-test-id="fitment-add-application-dialog">
        <DashboardModal.Title>
          {intl.formatMessage(fitmentMessages.addDialogTitle, {
            schema: schema.displayLabel || schema.name,
          })}
        </DashboardModal.Title>
        <Text size={2} color="default2">
          {intl.formatMessage(fitmentMessages.addDialogHint)}
        </Text>
        <Box display="flex" flexDirection="column" gap={4}>
          {axes.map(axis => (
            <AxisValueSelect
              key={axis.id}
              axis={axis}
              value={values[axis.id] ?? null}
              onChange={value => setValues(current => ({ ...current, [axis.id]: value }))}
            />
          ))}
          {qualifierValueOptions.length > 0 && (
            <Multiselect
              name="applicationQualifierValues"
              data-test-id="fitment-application-qualifiers"
              label={intl.formatMessage(fitmentMessages.applicationQualifiers)}
              options={filterOptions(qualifierValueOptions, qualifierQuery)}
              value={qualifiers}
              fetchOptions={setQualifierQuery}
              onChange={event => {
                const next = event.target.value as unknown as Option[];

                setQualifiers(current => onePerQualifier(current, next, qualifierOf));
              }}
            />
          )}
          {touched && picked.length === 0 && (
            <Text size={2} color="critical1">
              {intl.formatMessage(fitmentMessages.addDialogNeedsValue)}
            </Text>
          )}
          {errors.map((error, index) => (
            <Text key={index} size={2} color="critical1" data-test-id="fitment-add-error">
              {formatWsmError(error)}
            </Text>
          ))}
        </Box>
        <DashboardModal.Actions>
          <Button variant="secondary" onClick={onClose} data-test-id="cancel">
            {intl.formatMessage(buttonMessages.back)}
          </Button>
          <ConfirmButton
            transitionState={confirmButtonState}
            onClick={handleSubmit}
            data-test-id="submit"
          >
            {intl.formatMessage(buttonMessages.add)}
          </ConfirmButton>
        </DashboardModal.Actions>
      </DashboardModal.Content>
    </DashboardModal>
  );
};

export default ProductFitmentAddApplicationDialog;
